import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ApiService } from 'src/app/shared/services/api.service';
import { CadViewerDialogService } from 'src/app/shared/services/cad-viewer-dialog.service';
import { CadDrawing } from 'src/app/shared/services/cad-viewer.service';
import { CAD_FILE_ACCEPT } from '../../cad-viewer/cad-drawing-list/cad-drawing-list.component';

type DrawingField = 'gfcDrawings' | 'asBuiltDrawings';

@Component({
  selector: 'app-road-project-form2',
  templateUrl: './road-project-form2.component.html',
  styleUrls: ['./road-project-form2.component.css']
})
export class RoadProjectForm2Component implements OnInit {
  projectIdentificationForm!: FormGroup;
  roadSpecificationsForm!: FormGroup;
  siteConditionsForm!: FormGroup;
  designParametersForm!: FormGroup;
  legalComplianceForm!: FormGroup;
  stakeholdersForm!: FormGroup;
  projectTeamsForm!: FormGroup;
  contractorsForm!: FormGroup;
  equipmentForm!: FormGroup;
  projectControlsForm!: FormGroup;
  multiTenancyForm!: FormGroup;
  indianSpecificForm!: FormGroup;
  documentationForm!: FormGroup;
  accessLevels: any;

  readonly cadAccept = CAD_FILE_ACCEPT;
  drawingUploading: Partial<Record<DrawingField, boolean>> = {};
  drawingError: Partial<Record<DrawingField, string>> = {};
  private uploadedDrawings: Partial<Record<DrawingField, CadDrawing>> = {};
  private projectId: number | null = null;

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private api: ApiService,
    private cadViewerDialog: CadViewerDialogService
  ) {}
  ngOnInit(): void {
    const qp = this.route.snapshot.queryParamMap;
    const pid = Number(qp.get('project_id') ?? qp.get('id'));
    this.projectId = Number.isFinite(pid) && pid > 0 ? pid : null;

    this.projectIdentificationForm = this.fb.group({
      projectName: ['', Validators.required],
      projectType: ['', Validators.required],
      location: ['', Validators.required],
      roadClassification: ['', Validators.required],
      projectStage: ['', Validators.required],
      timeline: ['', Validators.required],
      projectOwner: ['', Validators.required],
      projectScheme: ['', Validators.required],
    });

    this.roadSpecificationsForm = this.fb.group({
      roadLength: ['', Validators.required],
      numberOfLanes: ['', Validators.required],
      laneWidth: ['', Validators.required],
      surfaceType: ['', Validators.required],
      designSpeed: ['', Validators.required],
      trafficVolume: ['', Validators.required],
      designLife: ['', Validators.required],
      ircCompliance: ['', Validators.required],
    });

    this.siteConditionsForm = this.fb.group({
      terrainType: ['', Validators.required],
    
      soilType: ['', Validators.required],
      cbrValue: ['', Validators.required],
      plasticityIndex: ['', Validators.required],
      moistureContent: ['', Validators.required],
    
      monsoonPattern: ['', Validators.required],
      annualRainfall: ['', Validators.required],
      floodHistory: ['', Validators.required],
      waterTableLevel: ['', Validators.required],
    
      temperatureRange: ['', Validators.required],
      frostSusceptibility: ['', Validators.required],
      coastalConsiderations: ['', Validators.required],
    });
    this.designParametersForm = this.fb.group({
      pavementType: ['', Validators.required],
      layerThicknesses: ['', Validators.required],
      materialSpecs: ['', Validators.required],
    
      horizontalAlignment: ['', Validators.required],
      verticalAlignment: ['', Validators.required],
      superElevation: ['', Validators.required],
      sightDistance: ['', Validators.required],
    
      sideDrains: ['', Validators.required],
      crossDrainageStructures: ['', Validators.required],
      culvertsBridges: ['', Validators.required],
    });

    this.legalComplianceForm = this.fb.group({
      landRequired: ['', Validators.required],
      landAcquired: ['', Validators.required],
      rfctlarrStatus: ['', Validators.required],
      pendingAcquisitions: ['', Validators.required],
      compensationStatus: ['', Validators.required],
      notificationStatus: ['', Validators.required],
    
      eiaStatus: ['', Validators.required],
      forestClearance: ['', Validators.required],
      wildlifeClearance: [''],
      crzClearance: [''],
    
      utilityElectrical: [''],
      utilityWater: [''],
      utilityTelecom: [''],
      utilityGas: [''],
      utilityRail: [''],
    
      rowClearance: [''],
      localPermissions: [''],
      treeCutting: [''],
      nocAuthorities: ['']
    });
    this.stakeholdersForm = this.fb.group({
      projectOwner: ['', Validators.required],
      regulatoryBodies: [[], Validators.required],
      fundingAgencies: [[], Validators.required],
      localCommunities: ['', Validators.required],
      propertyOwners: ['', Validators.required],
      localBodies: ['', Validators.required],
      districtAdmin: ['', Validators.required]
    });
    this.projectTeamsForm = this.fb.group({
      planningTeamStructure: ['', Validators.required],
      planningRoles: ['', Validators.required],
      planningContact: ['', Validators.required],
    
      designConsultants: [[], Validators.required],
      proofConsultants: ['', Validators.required],
      authorityEngineer: ['', Validators.required],
      designContact: ['', Validators.required],
    
      fieldManager: ['', Validators.required],
      fieldStructure: ['', Validators.required],
      fieldEngineers: ['', Validators.required],
      fieldSupervisors: ['', Validators.required],
      fieldContact: ['', Validators.required],
    
      acceptanceEngineer: ['', Validators.required],
      qualityEngineers: ['', Validators.required],
      nablLabs: ['', Validators.required],
      acceptanceContact: ['', Validators.required]
    });

    this.contractorsForm = this.fb.group({
      mainContractorName: ['', Validators.required],
      mainContractorLicense: ['', Validators.required],
      mainContractorCategory: ['', Validators.required],
      mainContractorContact: ['', Validators.required],
      performanceHistory: [''],
      bankGuarantee: [''],
    
      subcontractScope: [''],
      subcontractCompany: [''],
      subcontractContact: [''],
    
      materialType: ['', Validators.required],
      supplierName: ['', Validators.required],
      qualityCertifications: [''],
      supplierContact: [''],
    
      quarryLocations: [''],
      borrowDetails: [''],
      royaltyStatus: ['']
    });

    this.equipmentForm = this.fb.group({
      heavyType: ['', Validators.required],
      heavyModel: [''],
      heavyCapacity: [''],
      heavyOwnership: [''],
      maintenanceSchedule: [''],
      deploymentPlan: [''],
    
      testType: [''],
      calibrationStatus: [''],
      nablCertification: [''],
    
      toolsQuantity: [''],
      toolsCondition: [''],
      replacementSchedule: [''],
    
      plantCapacity: [''],
      pollutionCertificate: [''],
      plantCalibrationStatus: ['']
    });
    
    this.projectControlsForm = this.fb.group({
      totalCost: ['', Validators.required],
      costBreakdown: [''],
      fundingSources: [''],
      paymentSchedule: [''],
      milestonePayments: [''],
    
      wbs: [''],
      milestoneDates: [''],
      criticalPath: [''],
      monsoonPlanning: [''],
    
      testingFrequencies: [''],
      acceptanceCriteria: [''],
      qaPlan: [''],
      qualityAudit: [''],
    
      riskRegister: [''],
      mitigationStrategies: [''],
      contingencyPlans: ['']
    });
    this.multiTenancyForm = this.fb.group({
      tenantId: ['', Validators.required],
      accessControl: ['', Validators.required],
      dataSegregation: [''],
      tenantConfigs: [''],
      reportingRequirements: ['']
    });

    this.indianSpecificForm = this.fb.group({
      gstDetails: ['', Validators.required],
      taxDeduction: [''],
      labourLicense: [''],
      workmenInsurance: [''],
      pfEsi: [''],
      labourWelfare: [''],
      contractType: ['', Validators.required],
      arbitration: [''],
      conciliation: [''],
      disputeBoard: [''],
      makeInIndia: [''],
      msmeParticipation: [''],
      defectLiability: [''],
      routineMaintenance: [''],
      performanceMaintenance: ['']
    });

    this.documentationForm = this.fb.group({
      contractDocs: ['', Validators.required],
      dpr: ['', Validators.required],
      gfcDrawings: [''],
      progressReports: [''],
      qap: [''],
      testReports: [''],
      asBuiltDrawings: [''],
      omManuals: ['']
    });
    
    
    
    this.accessLevels = ['Admin', 'Editor', 'Viewer', 'Custom'];
    
    
    
    
    
  }

  uploadDrawing(field: DrawingField, event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;

    this.drawingUploading[field] = true;
    this.drawingError[field] = '';
    this.api
      .uploadCadDrawing(file, {
        contextType: 'project',
        contextId: field === 'gfcDrawings' ? 'gfc' : 'as_built',
        projectId: this.projectId,
      })
      .subscribe({
        next: (drawing) => {
          this.drawingUploading[field] = false;
          this.uploadedDrawings[field] = drawing;
          this.documentationForm.get(field)?.setValue(drawing.file_url);
          this.viewDrawing(field);
        },
        error: (err) => {
          this.drawingUploading[field] = false;
          this.drawingError[field] = err?.error?.error || 'Upload failed';
        },
      });
  }

  /** Only drawings uploaded here can be previewed; a pasted link is just stored. */
  canViewDrawing(field: DrawingField): boolean {
    const drawing = this.uploadedDrawings[field];
    return !!drawing && this.documentationForm?.get(field)?.value === drawing.file_url;
  }

  viewDrawing(field: DrawingField): void {
    const drawing = this.uploadedDrawings[field];
    if (drawing && this.canViewDrawing(field)) this.cadViewerDialog.open({ drawing });
  }

  onSubmit() {
    const projectData = {
      ...this.projectIdentificationForm.value,
      ...this.roadSpecificationsForm.value,
      ...this.siteConditionsForm.value,
      ...this.designParametersForm.value,
      ...this.legalComplianceForm.value,
      ...this.stakeholdersForm.value,
      ...this.projectTeamsForm.value,
      ...this.contractorsForm.value,
      ...this.equipmentForm.value,
      ...this.projectControlsForm.value,
      ...this.multiTenancyForm.value,
      ...this.indianSpecificForm.value,
      ...this.documentationForm.value
    };
    console.log('Submitted Project:', projectData);
    // Optionally send to backend
  }

}
